import { HttpClient } from "@angular/common/http";
import { inject, Injectable, Injector, NgZone } from "@angular/core";
import { BehaviorSubject, Observable, ReplaySubject } from "rxjs";
import { first, tap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { CountryAgencyModel } from "../../models/country-agency-mode";

@Injectable({ providedIn: "root" })
export class TopBarService {
  private currentUserDataPrefSubject: BehaviorSubject<CountryAgencyModel[]> =
    new BehaviorSubject<CountryAgencyModel[]>(
      JSON.parse(localStorage.getItem("currentUserDataPref") || "[]")
    );
  private http = inject(HttpClient);
  private selectedCountry = new BehaviorSubject<boolean>(false);
  // updatedCountry$ = this.selectedCountry.asObservable();
  previousCountrySelected: CountryAgencyModel | undefined;
  countrySelected: CountryAgencyModel | undefined;
  private syncSubject = new ReplaySubject<void>(1);
  private alreadySynced = false;

  constructor(private zone: NgZone) {}

  getCountriescontainsProperties(): Observable<any> {
    return this.http
      .get(`${environment.apiUrl}/counties/locations/contains-properties`, {
        withCredentials: true,
      })
      .pipe(first());
  }

  updateCountryWithAgencies(): Observable<any> {
    return this.http
      .post(`${environment.apiUrl}/counties/locations/agencies`, {
        withCredentials: true,
      })
      .pipe(first());
  }

  saveUserDataPrefToLocalStorage(countryAgencyModel: CountryAgencyModel) {
    let current = this.currentUserDataPrefSubject.value;
    const exists = current.some(
      (c) =>
        c.user === countryAgencyModel.user &&
        c.entity === countryAgencyModel.entity
    );
    if (exists) {
      current = current.filter(
        (item) =>
          item.user !== countryAgencyModel.user &&
          item.entity !== countryAgencyModel.entity
      );
    }
    const updated = [...current, countryAgencyModel];
    localStorage.setItem("currentUserDataPref", JSON.stringify(updated));
    this.currentUserDataPrefSubject.next(updated);
  }

  removeUserDataPrefToLocalStorage(userId: any, entityId: any) {
    const current = this.currentUserDataPrefSubject.value;
    const updated = current.filter(
      (item) => item.user.id !== userId && item.entity !== entityId
    );
    localStorage.removeItem("currentUserDataPref");
    this.currentUserDataPrefSubject.next(updated);
  }

  getUserDataPref() {
    return this.currentUserDataPrefSubject.value;
  }
  
  update(user: any) {
    const usersDataPref = this.getUserDataPref();

    if (usersDataPref) {
      const udp = usersDataPref.find(
        (ud) => ud.user.id === user.id && ud.entity === user.entity
      );
      if (udp) {
        const dataCountry = {
          id: udp.id,
          agencies: udp.agencies?.map((ag) => ag.id),
          user: udp.user,
          entity: udp.entity,
        };

        this.http
          .post<void>(
            `${environment.apiUrl}/auth/user-data-pref`,
            { dataCountry },
            { withCredentials: true }
          )
          .pipe(
            first(),
            tap(() => {
              this.alreadySynced = true;
              this.syncSubject.next(); // notifica todos os outros componentes
            })
          )
          .subscribe();
      } else {
        this.http
          .post<void>(
            `${environment.apiUrl}/auth/user-data-pref`,
            {},
            { withCredentials: true }
          )
          .pipe(
            first(),
            tap(() => {
              this.alreadySynced = true;
              this.syncSubject.next(); // notifica todos os outros componentes
            })
          )
          .subscribe();
      }
    }
  }

  triggerSyncIfNeeded(user: any) {
    if (!this.alreadySynced) {
      this.update(user);
    } else {
      this.syncSubject.next(); // já sincronizado ou sem alterações → notifica direto
      this.alreadySynced = true;
    }
  }

  waitForSync(): Observable<void> {
    return this.syncSubject.asObservable(); // os outros componentes escutam isto
  }

  checkChanges(): boolean {
    return !this.deepEqual(this.countrySelected, this.previousCountrySelected);
  }

  deepClone<T>(obj: T): T {
    if (obj === null || typeof obj !== "object") return obj;

    if (obj instanceof Date) return new Date(obj.getTime()) as any;

    if (Array.isArray(obj))
      return obj.map((item) => this.deepClone(item)) as any;

    const clone: any = {};
    for (const key in obj) {
      clone[key] = this.deepClone((obj as any)[key]);
    }

    return clone;
  }

  deepEqual(a: any, b: any): boolean {
    if (a === b) return true;

    if (typeof a !== typeof b) return false;

    if (typeof a !== "object" || a === null || b === null) return false;

    if (Array.isArray(a) !== Array.isArray(b)) return false;

    const keysA = Object.keys(a);
    const keysB = Object.keys(b);

    if (keysA.length !== keysB.length) return false;

    for (const key of keysA) {
      if (!keysB.includes(key)) return false;
      if (!this.deepEqual(a[key], b[key])) return false;
    }

    return true;
  }

  getLatest(): Observable<any[]> {
    return this.http
      .get<any[]>(`${environment.apiUrl}/notifications/latest`, {
        withCredentials: true,
      })
      .pipe(first());
  }
}
